import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button
} from '@mui/material';
import {
  TrendingUp,
  TouchApp,
  AttachMoney,
  Assessment
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/apiService';

const StatCard = ({ title, value, icon, color = 'primary' }) => (
  <Card elevation={2}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography color="textSecondary" gutterBottom variant="overline">
            {title}
          </Typography>
          <Typography variant="h4" component="div">
            {value}
          </Typography>
        </Box>
        <Box sx={{ color: `${color}.main` }}>
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const DashboardPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');


  useEffect(() => {
    const fetchStats = async () => {
      try {
        if (user?.id) {
          const statsData = await apiService.getAffiliateStats(user.id);
          setStats(statsData);
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [user]);


  const calculateTotals = (statsData) => {
    if (!statsData) return { clicks: 0, conversions: 0, revenue: 0, conversionRate: 0 };

    const clicks = statsData.clickStats?.reduce((total, stat) => total + stat.count, 0) || 0;
    const conversions = statsData.clickStats?.find(stat => stat._id === 'converted')?.count || 0;
    const revenue = statsData.conversionStats?.reduce((total, stat) => total + stat.totalPayout, 0) || 0;
    const conversionRate = clicks > 0 ? ((conversions / clicks) * 100).toFixed(2) : 0;

    return { clicks, conversions, revenue, conversionRate };
  };

  const totals = calculateTotals(stats);

  if (loading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Typography variant="h4" component="h1" gutterBottom>
        Welcome back, {user?.email}!
      </Typography>

      <Typography variant="subtitle1" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
        Here's an overview of your affiliate performance
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Stats Cards */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Clicks"
            value={totals.clicks.toLocaleString()}
            icon={<TouchApp fontSize="large" />}
            color="primary"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Conversions"
            value={totals.conversions.toLocaleString()}
            icon={<TrendingUp fontSize="large" />}
            color="success"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Revenue"
            value={`$${totals.revenue.toFixed(2)}`}
            icon={<AttachMoney fontSize="large" />}
            color="warning"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Conversion Rate"
            value={`${totals.conversionRate}%`}
            icon={<Assessment fontSize="large" />}
            color="info"
          />
        </Grid>

        {/* Top Offers */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Top Performing Offers
            </Typography>
            {stats?.topOffers && stats.topOffers.length > 0 ? (
              <Box sx={{ mt: 2 }}>
                {stats.topOffers.slice(0, 5).map((offer, index) => (
                  <Box
                    key={offer.offerId}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 1,
                      borderBottom: index < 4 ? 1 : 0,
                      borderColor: 'divider'
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle2">
                        {offer.offerTitle}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {offer.clicks} clicks • {offer.conversions} conversions
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" fontWeight="bold">
                        ${offer.totalPayout.toFixed(2)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {offer.conversionRate.toFixed(1)}% CR
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                No offers data available yet. Start promoting offers to see your performance here.
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                • Browse available offers
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                • Generate affiliate links
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                • View detailed reports
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                • Download embed codes
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default DashboardPage;
